import { getBaseRole } from '@/helpers/customRoles';
import React, { useEffect } from "react";
import { ProfileCard } from "./ProfileCard";
import { directionOrdersApi, profileFieldsApi, usersAPI } from "../../../api";
import { createDate } from "../../../helpers/date";
import { getInArr } from "../../../helpers/getInArr";
import { useDispatch, useSelector } from "react-redux";
import { actionUpdProfile, selectProfile } from "@store/profile.reducer";
import Preloader from "../../UI/Preloader";
import { useAddAlert } from "../../../hooks/useAddAlert";
import { getEmployeeProfileFields } from "../../../helpers/employeeProfileFields";
import {
  PROFILE_FIELD_ENTITY_TYPES,
  getProfileFieldValuesPayload,
  getProfileFieldValuesResponseData,
  getProfileFieldsResponseData,
  setProfileFieldValue,
} from "../../../helpers/profileFields";

const ProfileCardContainer = ({
  userId,
  type,
  editMode = false,
  hideEditMode,
  editProfile,
  isEmployee,
}) => {
  
  const profile = useSelector(selectProfile);
  const settings = useSelector((store) => store.settings)
  const loadingSettings = settings.status < 2;
  const isTelebosIntegrationEnabled = settings?.isTelebosIntegrationEnabled == '1' || settings?.isTelebosIntegrationEnabled === true;

  const isTeacherScheduleModificationRestricted = (settings?.isTeacherScheduleModificationRestricted==1 && getBaseRole(profile.role)=='seminarian');
  const hidePasswordChange = (settings?.isStudentCannotChangeHisOrHerPassword==1 && getBaseRole(profile.role)=='children') || loadingSettings;
  const isSelfServiceAvatarRole = ['children', 'seminarian', 'parent'].includes(getBaseRole(profile.role));
  const hideAvatarChange =
    (loadingSettings && isSelfServiceAvatarRole) ||
    (settings?.isStudentCannotChangeAvatar == 1 && getBaseRole(profile.role) == 'children') ||
    (settings?.isTeacherCannotChangeAvatar == 1 && getBaseRole(profile.role) == 'seminarian') ||
    (settings?.isParentCannotChangeAvatar == 1 && getBaseRole(profile.role) == 'parent');

  const dispatch = useDispatch();

  const [user, setUser] = React.useState({});
  const [userOld, setUserOld] = React.useState({});
  const [profileApiFields, setProfileApiFields] = React.useState([]);
  const [customProfileFieldValues, setCustomProfileFieldValues] =
    React.useState({});
  const [areTeacherProfileFieldsLoaded, setAreTeacherProfileFieldsLoaded] =
    React.useState(false);
  const isTeacherOwnProfile =
    type === "personal" &&
    isEmployee;
  const teacherProfileFields = areTeacherProfileFieldsLoaded
    ? getEmployeeProfileFields(settings, profileApiFields).filter(
        (field) => field.type !== "technical",
      )
    : null;

  const getTeacherProfileField = (fieldId) =>
    teacherProfileFields?.find((field) => field.id === fieldId);
  const isTeacherProfileFieldVisible = (fieldId) =>
    !teacherProfileFields ||
    Boolean(getTeacherProfileField(fieldId)?.profile_visible);
  const canEditTeacherProfileField = (fieldId) =>
    !teacherProfileFields ||
    Boolean(getTeacherProfileField(fieldId)?.profile_editable);
  const isTeacherProfileFieldRequired = (fieldId) =>
    !teacherProfileFields
      ? fieldId === "email"
      : Boolean(getTeacherProfileField(fieldId)?.required);
  const editCustomProfileField = (field, value) =>
    setCustomProfileFieldValues((prev) =>
      setProfileFieldValue(prev, field, value),
    );

  //получаем пользователя
  const getUser = () =>
    usersAPI
      .getUser({
        id: userId,
        expand: ["userProfile", "roles", "contracts", 'workHours'],
      })
      .then((res) => {
        const data = {};
        if (res.data?.id !== undefined)
          for (let key in res.data) {
            if (key === "userProfile")
              for (let y in res.data.userProfile)
                data[y] = res.data.userProfile[y];
            else data[key] = res.data[key];
          }
        setUser(data);
        setUserOld(data);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (userId !== user.id) getUser();
  }, []);

  React.useEffect(() => {
    if (!isTeacherOwnProfile) return;

    profileFieldsApi
      .index({ entity_type: PROFILE_FIELD_ENTITY_TYPES.EMPLOYEE })
      .then((response) => {
        const fields = getProfileFieldsResponseData(response);

        setProfileApiFields(fields);
        setAreTeacherProfileFieldsLoaded(true);
      })
      .catch(() => {
        setProfileApiFields([]);
        setAreTeacherProfileFieldsLoaded(true);
      });
  }, [isTeacherOwnProfile]);

  React.useEffect(() => {
    if (!isTeacherOwnProfile || !user.id) return;

    profileFieldsApi
      .values({
        user_id: user.id,
        entity_type: PROFILE_FIELD_ENTITY_TYPES.EMPLOYEE,
      })
      .then((response) => {
        setCustomProfileFieldValues(
          getProfileFieldValuesResponseData(response),
        );
      })
      .catch(() => setCustomProfileFieldValues({}));
  }, [isTeacherOwnProfile, user.id]);

  // изменение данных
  const edit = (key, val) => setUser({ ...user, [key]: val });

  // информация для карточки
  const [settingCard, setSettingCard] = React.useState({
    type,
    editMode: type === "personal" ? true : editMode,
  });
  const setEditMode = (edit = true) =>
    setSettingCard({ ...settingCard, editMode: edit });

  const addAlert = useAddAlert();

  // модалка
  const closeModal = () =>
    setModalOptions({
      type: "",
      data: {},
      active: false,
      ...constDataModal,
    });
  const setModal = (type, data) =>
    setModalOptions({ type, data, active: true, ...constDataModal });
  const constDataModal = {
    state: user,
    setState: setUser,
    refreshUser: getUser,
    close: closeModal,
    setModal,
    addAlert,
  };
  const [modalOptions, setModalOptions] = React.useState({
    type: "",
    data: {},
    active: false,
    ...constDataModal,
  });

  // дата регистрации
  const createdAt = createDate({ date: new Date(user.created_at * 1000) });

  // роли пользователя
  const role =
    user.id !== undefined && user.roles?.length && user.roles[0].item_name;
  // const rolesList = user.id !== undefined && user.roles?.length && user.roles
  const rolesName =
    user.id !== undefined && user?.roles?.map((i) => i.item_name);

  // направления
  const [childrenDirection, setChildrenDirection] = React.useState([]);
  const getDirection = () =>
    directionOrdersApi
      .get({
        filter: [`[children_id][eq]=${user.id}`, `[is_disabled][eq]=0`],
        expand: [
          "direction",
          "direction.directionTariffs",
          "group",
          "teacher",
          "privateInfo",
          'direction.mediaIcon',
        ],
        pageSize: false,
      })
      .then((res) => {
        setChildrenDirection(
          res.data?.data?.map((direction) => {
            if (direction.teacher_id) return direction;
            else return { ...direction, teacher: direction.group };
          }) ?? []
        );
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!isEmployee && user.id) {
      getDirection();
    }
  }, [role]);

  // приватные направления
  const [privateInfo, setPrivateInfo] = React.useState([]);

  React.useEffect(() => {
    const newPrivateInfo = [];

    childrenDirection?.forEach(
      (order) => order?.privateInfo?.id && newPrivateInfo.push(order)
    );

    setPrivateInfo(newPrivateInfo);
  }, [childrenDirection]);

  // направление семинариста
  const getSeminarianDirection = () =>
    usersAPI
      .directionBySeminarian({
        id: user.id,
        params: { expand: "direction" },
      })
      .then((res) => {
        setUser((prev) => ({
          ...prev,
          seminarianDirection:
            res?.data?.data.map(({ id: linkId, direction: { name, id }, rate, rate_group }) => ({
              id,
              name,
              linkId,
              rate,
              rate_group,
            })) ?? [],
        }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isEmployee && user.id !== undefined) {
      getSeminarianDirection();
    }
  }, [user.id]);

  // валидация
  const [errorsList, setErrorsList] = React.useState([]);
  const [showErrors, setShowErrors] = React.useState(false);

  // отмена
  const cancel = () => {
    setUser(userOld);
    setEditMode(false);
  };
  // поля дня отправки
  const params = [
    "name",
    "login",
    "email",
    "phone",
    "roles",
    "about",
    "city",
    "address",
    "education",
    "experience",
    "learning_class",
    "link_to_vk",
    "notes",
    "parent_name",
    "parent_phone",
    "parent_telegram",
    "parent_whatsapp",
    "sex",
    "timezone",
    "birth_date",
    "url_conference",
    "telebos_room_id",
    "about_student",
    "custom_id",
  ];

  // сохранение
  const [saves, setSaves] = React.useState(false);
  const save = async () => {
    setShowErrors(true);
    if (
      isEmployee &&
      ![user.email, user.phone, user.login].some((value) =>
        String(value ?? "").trim(),
      )
    ) {
      addAlert({ type: "error", title: "Укажите почту, телефон или логин" });
      return;
    }
    if (
      isEmployee &&
      isTeacherProfileFieldRequired("email") &&
      !String(user.email ?? "").trim()
    ) {
      addAlert({ type: "error", title: "Укажите электронную почту" });
      return;
    }
    if (errorsList.valid || errorsList === [] || !errorsList.items || (errorsList && errorsList.items && Object.keys(errorsList.items).length < 2 && errorsList.items.email)) {
      const data = () => {
        const newData = {};
        for (let key in user) {
          if (getInArr(key, params)) {
            if (
              ["email", "phone", "login"].includes(key) &&
              user[key] !== userOld[key]
            ) {
              newData[key] = user[key] ?? "";
            } else if (user[key]) newData[key] = user[key];
            if ([key] == 'custom_id' && userOld[key] && !user[key]) newData[key] = null;
            if (key === 'telebos_room_id' && userOld[key] && !user[key]) newData[key] = null;
          }
        }
        if ('custom_fields' in user)
          newData.custom_fields = JSON.stringify(user.custom_fields);
        newData.notes = [];
        return newData;
      };

      usersAPI
        .upd({ id: user.id, data: data(), expand: ["roles"] })
        .then(async (res) => {
          if (res.data.status === "success") {
            if (teacherProfileFields) {
              try {
                await profileFieldsApi.saveValues({
                  user_id: user.id,
                  entity_type: PROFILE_FIELD_ENTITY_TYPES.EMPLOYEE,
                  custom_fields: getProfileFieldValuesPayload(
                    teacherProfileFields.filter((field) => field.isCustom),
                    customProfileFieldValues,
                  ),
                });
              } catch {
                addAlert({
                  type: "error",
                  title: "Не удалось сохранить дополнительные поля профиля",
                });
                return;
              }
            }
            addAlert({ title: "Профиль изменен" });
            setSaves(true);
            setTimeout(() => setSaves(false), 3000);
            window.location.reload();

            if (type !== "personal") setEditMode(false);
            else editProfile({ ...user, role });
          } else {
            addAlert({
              type: "error",
              title:
                "Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору",
            });
          }
        });

      if (user.id === profile.id) {
        dispatch(
          actionUpdProfile({
            ...profile,
            userProfile: {
              ...profile.userProfile,
              url_conference: user.url_conference,
              telebos_room_id: user.telebos_room_id,
            },
          })
        );
      }
    }
  };

  // пропсы для карточки
  const propsCard = {
    settingCard: { ...settingCard, setEditMode },
    user: { ...user, createdAt, role, rolesName, childrenDirection },
    modalOptions: { ...modalOptions, getDirection },
    privateInfo,
    setModal,
    closeModal,
    cancel,
    save,
    edit,
    saves,
    userRole: profile.role,
    validate: { setErrorsList, showErrors },
    hideEditMode,
    isEmployee,
    isTeacherScheduleModificationRestricted,
    hidePasswordChange,
    hideAvatarChange,
    isTelebosIntegrationEnabled,
    teacherProfileFields,
    customProfileFieldValues,
    editCustomProfileField,
    isTeacherProfileFieldVisible,
    canEditTeacherProfileField,
    isTeacherProfileFieldRequired,
  };

  return user.id === undefined
    ? <Preloader fullScreen="container" />
    : <ProfileCard {...propsCard} />
};

export default ProfileCardContainer;
